import { type ExtensionAPI } from '@atlaskit/editor-common/extensions';
import { type ForgeExtensionProvider } from './forgeExtensionProvider';
import {
	type ConnectToForgeParameters,
	type ForgeCreateExtensionProps,
	type ForgeExtensionManifest,
	type ForgeExtensionParameters,
	type ForgeExtensionProviderSharedParams,
} from '../types';

import {
	P2_OR_CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE,
	EXTENSION_TYPE_MULTI_BODIED,
} from '../utils/constants';
import { getConnectMacroId } from '../utils/getConnectMacroId';
import { AnalyticsAction, type AnalyticsClient } from '../utils/analytics';
import { fg } from '@atlaskit/platform-feature-flags/fg';

export type UpdateADFEntity = Parameters<Parameters<ExtensionAPI['doc']['update']>[1]>[0];

export const upgradeConnectNodeToForge = (
	node: UpdateADFEntity,
	extensionManifest: ForgeExtensionManifest,
): UpdateADFEntity => {
	const { attrs } = node;
	// Ensure extension hasn't been upgraded since the last check
	if (attrs?.extensionType === P2_OR_CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE) {
		// Use the existing Connect macro ID as the Forge localId
		const macroId = getConnectMacroId(attrs.parameters);
		const extensionProps = extensionManifest?.createExtensionProps(macroId);
		return {
			...node,
			attrs: {
				...attrs,
				...extensionProps.attrs,
				// We shouldn't override the Editor localId of an existing node once it's been created
				localId: attrs.localId || extensionProps.attrs.localId,
				parameters: {
					...convertConnectParametersToForge(attrs.parameters),
					...extensionProps.attrs.parameters,
				},
			},
		};
	}
	return node;
};

const convertConnectParametersToForge = ({
	guestParams = {},
	macroParams = {},
	...restParams
}: ConnectToForgeParameters = {}): ConnectToForgeParameters => {
	const migratedGuestParams = { ...guestParams };
	Object.entries(macroParams).forEach(([key, obj]) => {
		// Don't override existing entries
		if (typeof migratedGuestParams[key] === 'undefined' && obj && 'value' in obj) {
			migratedGuestParams[key] = obj.value;
		}
	});

	if (fg('adf_export_dynamic_macro_render_bug_fix')) {
		// Strip macroMetadata so the upgraded node matches a natively-created Forge macro. While it
		// is present, the Confluence backend (MacroServiceImpl.readMacroId) extracts the node for
		// server-side render and invokes adfExport on every page view (CONFCLOUD-83809). macroParams
		// is kept: the frontend derives guestParams from it and the backend does not key on it.
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { macroMetadata, ...restWithoutMacroMetadata } = restParams;
		return {
			macroParams,
			guestParams: migratedGuestParams,
			...restWithoutMacroMetadata,
		};
	}

	return {
		macroParams,
		guestParams: migratedGuestParams,
		...restParams,
	};
};

type UpdatedNode = { originalMacroType: 'P2' | 'CONNECT'; updatedNode: UpdateADFEntity };

const upgradeConnectExtensionToForge = async (
	attrs: Record<string, string>,
	actions: ExtensionAPI<ForgeExtensionParameters>,
	extensionProvider: ForgeExtensionProvider,
): Promise<UpdatedNode> => {
	const { localId, extensionKey } = attrs;

	let extensionManifest = await extensionProvider.getConnectToForgeExtension(extensionKey);
	let originalMacroType: 'P2' | 'CONNECT' = 'CONNECT';

	if (!extensionManifest && fg('platform_upgrade_p2_to_forge_macro')) {
		const p2ToForgeExtensionManifest = await extensionProvider.getP2ToForgeExtension(extensionKey);
		originalMacroType = 'P2';
		extensionManifest = p2ToForgeExtensionManifest;
	}

	if (!extensionManifest || !extensionManifest.createExtensionProps) {
		throw new Error(
			`Connect to Forge macro upgrade: No ${
				!extensionManifest ? 'extension manifest' : 'createExtensionProps function'
			} found`,
		);
	}
	return new Promise<UpdatedNode>((resolve) => {
		actions.doc.update(localId, (currentValue) => {
			const updatedNode = upgradeConnectNodeToForge(currentValue, extensionManifest);
			resolve({ updatedNode, originalMacroType });
			return updatedNode;
		});
	});
};

export const maybeUpgradeConnectExtensionToForge = async ({
	connectToForgeBlockedExtensions,
	extensionProvider,
	actions,
	analyticsClient,
	editorActions,
}: {
	actions?: ExtensionAPI<ForgeExtensionParameters>;
	analyticsClient: AnalyticsClient;
	connectToForgeBlockedExtensions: Set<unknown>;
	extensionProvider: ForgeExtensionProvider;
} & Pick<ForgeExtensionProviderSharedParams, 'editorActions'>): Promise<void> => {
	// Only passed through when we are in the Editor view
	if (!editorActions) {
		return;
	}

	const { sendEvent } = analyticsClient;

	const node = editorActions.getSelectedNode();
	const attrs = node?.attrs;
	if (attrs?.extensionType === P2_OR_CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE) {
		const { extensionKey, localId: nodeLocalId } = attrs;
		const macroId = getConnectMacroId(attrs.parameters);
		const extensionType = node?.type.name;

		if (extensionType === EXTENSION_TYPE_MULTI_BODIED) {
			// Upgrade to forge blocked because:
			//     Extension is multi-bodied which forge doesn't support
			connectToForgeBlockedExtensions.add(macroId);
			sendEvent(AnalyticsAction.H11N_UPGRADE_RICH_TEST_SKIPPED, {
				nodeLocalId,
			});
			return;
		}

		/* At this point, we know the extension should be upgraded from Connect to Forge
		 * If anything goes wrong after this point, it should be logged as an upgrade failure */
		try {
			if (!actions?.doc?.update) {
				throw new Error('Connect to Forge macro upgrade: Missing ExtensionAPI update action');
			}

			const { updatedNode, originalMacroType } = await upgradeConnectExtensionToForge(
				attrs,
				actions,
				extensionProvider,
			);

			const { localId, extensionId } = (updatedNode.attrs as ForgeCreateExtensionProps['attrs'])
				.parameters;

			if (originalMacroType === 'CONNECT') {
				sendEvent(AnalyticsAction.H11N_UPGRADE_SUCCESS, {
					extensionId,
					extensionKey,
					localId,
					nodeLocalId,
					extensionType,
				});
			} else if (originalMacroType === 'P2') {
				sendEvent(AnalyticsAction.APP_MIGRATION_P2_MACRO_UPGRADE, {
					extensionId,
					extensionKey,
					localId,
					nodeLocalId,
					extensionType,
				});
			}
		} catch (error) {
			sendEvent(AnalyticsAction.H11N_UPGRADE_FAILED, {
				extensionKey,
				error,
				nodeLocalId,
				extensionType,
			});
			throw error;
		}
	}
};
